import {
  FlightRawEventName,
  useEmitCoreFlightRawEventOnce,
  useFlightSlot,
} from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { Page } from "@b2bportal/core-ui";
import {
  FilterDialog,
  Filters,
  FlightList,
  FlightListError,
  FlightListLoading,
  useTrackEventRuntime,
  type DeparturePageProps,
} from "@tenant/flights";
import clsx from "clsx";
import defaultStyles from "./DeparturePage.module.scss";
import { useRbcSlot } from "@/components/slots";

const COMPONENT_KEY = CoreFlightsComponent.DeparturePage;

export const DeparturePage = ({
  children,
  className,
  context,
  handlers,
  text,
}: DeparturePageProps) => {
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const FlightSlot = useFlightSlot();

  const {
    isLoading,
    tripCategory,
    destination,
    sortOption,
    isError,
    flightList,
    isInitializing,
    isAllCollapsed,
    isFixedPriceRoute,
    pricingMode,
  } = context;

  useEmitCoreFlightRawEventOnce(
    {
      name: FlightRawEventName.EnteredFlight,
      properties: { first_launch: true },
    },
    isInitializing === false // Wait for state to populate when reloading
  );

  useTrackEventRuntime(
    {
      name: FlightRawEventName.ViewedFlightListLoading,
      properties: {},
    },
    isLoading
  );

  const {
    handleSortChange,
    handleFareSelect,
    handleChooseFare,
    onErrorReload,
    setIsAllCollapsed,
  } = handlers;

  const RbcSlot = useRbcSlot();

  return (
    <Page
      title={text.pageTitle}
      subtitle={text.subtitle}
      className={clsx(block, className)}
    >
      <div className={cn("container")}>
        <div className={cn("search")}>
          <FlightSlot.FlightShopSearch />
        </div>
        <div className={cn("filter")}>
          <Filters sliceIndex={0} />
        </div>

        {!isLoading && (
          <>
            <RbcSlot.DeparturePageHeader
              tripCategory={tripCategory}
              destination={destination}
              isError={isError}
              isFixedPriceRoute={isFixedPriceRoute}
              pricingMode={pricingMode}
              flightListLength={flightList.length}
              sortOption={sortOption}
              handleSortChange={handleSortChange}
              setPricingMode={handlers.setPricingMode}
            />

            {isError ? (
              <FlightListError
                className={cn("error")}
                onErrorReload={onErrorReload}
              />
            ) : (
              <div
                className={clsx(
                  cn("flight-list-container", {
                    initializing: isInitializing ?? false,
                  })
                )}
              >
                <FlightList
                  flightList={flightList}
                  sliceIndex={0}
                  onFareSelect={handleFareSelect}
                  goToNextStep={handleChooseFare}
                  className={cn("flight-list")}
                  isAllCollapsed={isAllCollapsed}
                  setIsAllCollapsed={setIsAllCollapsed}
                />
              </div>
            )}
            <div className={cn("all-filters")}>
              <FlightSlot.FilterDialog
                sliceIndex={0}
                sortOption={sortOption}
                handleSortChange={handleSortChange}
              />
            </div>
          </>
        )}
        <FlightListLoading isLoading={isLoading || isInitializing} />
        {children}
      </div>
    </Page>
  );
};
